import React from 'react';
import { connect } from 'react-redux';
import { DateTime } from 'luxon';

import withStyles from '@mui/styles/withStyles';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import RadioGroup from '@mui/material/RadioGroup';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

// import HideIcon from '@mui/icons-material/Close';
import CollapseIcon from '@mui/icons-material/ExpandMore';

import { tooltip } from '../assets/jss/material-dashboard-pro-react.jsx';
import { allevaOrangeColor } from '../md/assets/jss/material-dashboard-pro-react.jsx';

import { addOrUpdateVobFormInfo } from '../store/slices/vobFormInfosSlice';
import { updateFirmDropdown } from '../store/slices/firmInfoSlice';

import { createStdRequest, apiFetch, calculatePhoneNumberChange, sortedDropdownItemLabels } from '../utils/utils';

import DA from '../store/ReduxAccess';

import KSelect from '../components/KSelect';
import StateSelector from '../components/StateSelector';

import prefabCarrierNames from '../assets/text/carrierNames';
import ConfirmModal from './ConfirmModal';
import PromptModal from './PromptModal';
import CrmTextField from './CrmTextField';
import CrmButton from './CrmButton';
import CrmRadio from './CrmRadio';
import KDatePicker from './KDatePicker';

const fourBorders = false;

const localStyles = (theme) => ({
  vobFormTitleLine: {
    paddingBottom: '6px',
    marginBottom: '4px',
    borderBottom: '1px solid #eee',
  },
  topCloseIcon: {
    float: 'right',
  },
  topCloseIcon2: {
    display: 'inline-block',
    width: '30px',
  },
  tooltip,
  collapseButton: {
    margin: '0 0 0 -20px',
    transform: 'scale(1.3)',
    '& svg,& .fab,& .fas,& .far,& .fal,& .material-icons': {
      marginRight: '0px',
    },
  },
  icon: {
    verticalAlign: 'middle',
    width: '17px',
    height: '17px',
    top: '-1px',
    position: 'relative',
  },
  vobFormTitleDiv: {
    fontSize: '24px',
    marginLeft: '16px',
    marginTop: '20px',
    paddingBottom: '12px',
  },
  vobFormTitleDiv2: {
    display: 'inline-block',
    fontSize: '24px',
    marginLeft: '16px',
    marginTop: '20px',
    paddingBottom: '12px',
  },
  vobForm: {
    paddingLeft: '8px',
    paddingRight: '12px',
    fontWeight: 400,
    color: '#666',
    border: '2px solid black',
    margin: '2px',
  },
  vobFormBody: {
    fontSize: '1rem',
  },
  vobSwitchRound: {
    '& .MuiSwitch-colorPrimary.Mui-checked': {
      color: `${allevaOrangeColor[0]} !important`,
    },
    '& .MuiSwitch-colorPrimary.Mui-checked + .MuiSwitch-track': {
      backgroundColor: allevaOrangeColor[0],
      opacity: '0.5',
    },
  },
  vobCheckBoxStyle: {
    '& .MuiCheckbox-colorPrimary.Mui-checked': {
      color: `${allevaOrangeColor[0]} !important`,
    },
  },
  separator: {
    marginTop: '4px',
    width: '100%',
    height: '4px',
    borderTop: '1px solid gray',
    borderBottom: '1px solid gray',
  },
  verificationTable: {
    paddingBottom: '10px',
    '&>tbody>tr>td': {
      paddingLeft: '20px',
    },
  },
  snuggleDatum: {
    paddingTop: '14px',
  },
  table2: {
    '& td': {
      paddingLeft: '20px',
    },
  },
  policyHolderBDayDatum: {
    paddingBottom: '20px',
  },
  statePickerDatum: {
    paddingBottom: '6px',
  },
  table3_1: {
    '& td': {
      paddingLeft: '20px',
    },
  },
  policyYearRadios: {
    transform: 'scale(0.85)',
  },
  policyPropCheckBoxes: {
    transform: 'scale(0.85)',
  },
  planTypeSelectHolder: {
    width: '116px',
  },
  reimbursementBasisSelectHolder: {
    width: '116px',
  },
  table3_2: {
    '& td': {
      paddingLeft: '20px',
    },
  },
  table3_3: {
    '& td': {
      padding: '10px 0 0 20px',
    },
  },
  table3_4: {
    '& td': {
      padding: '10px 0 0 20px',
    },
  },
  networkStatusSelectDatum: {
    width: '150px',
  },
  table3_5: {
    '& td': {
      paddingLeft: '20px',
    },
  },
  table4: {
    '& td': {
      padding: '0 10px',
    },
  },
  table5: {
    marginTop: '16px',
    '& td': {
      padding: '0 10px',
    },
  },
  table5ColumnHeaders: {
    fontWeight: 500,
  },
  textField: {
    // marginTop: "-10px",
    '& label': {
      fontSize: '0.85rem',
    },
    '&.empty label': {
      transform: 'translate(0, 18px)',
    },
    '&.empty.focused label': {
      transform: 'translate(0, 1px) scale(0.75)',
    },
    '&.mw70px': {
      minWidth: '70px',
    },
    '&.mw120px': {
      minWidth: '120px',
    },
    '&.mw200px': {
      minWidth: '200px',
    },
    '&.mw240px': {
      minWidth: '240px',
    },
    '&.mw700px': {
      minWidth: '700px',
    },
    '&.fw70px': {
      width: '70px',
    },
    '&.fw80px': {
      width: '80px',
    },
  },
  textFieldLabel: {
    display: 'inline-block',
    marginTop: '6px',
  },
  textFieldInput: fourBorders
    ? {
        fontSize: '14px',
        fontWeight: 400,
        border: '1px solid gray',
        padding: '1px 1px 1px 4px',
        margin: '6px 0 0',
      }
    : {
        fontSize: '14px',
        fontWeight: 400,
        // border: "1px solid gray",
        paddingLeft: '4px',
        padding: '1px',
      },
  vobSwitch: {
    height: '20px',
  },
  locLabel: {
    textAlign: 'right',
  },
  notesHolder: {
    marginTop: '30px',
    marginLeft: '10px',
    marginBottom: '20px',
  },
  notesField: {
    width: '98%',
  },
  notesFieldInput: fourBorders
    ? {
        fontSize: '12px',
        // color: "green",
        fontWeight: 400,
        border: '1px solid gray',
        padding: '1px 1px 1px 4px',
        margin: '-6px 0 -7px',
      }
    : {
        fontSize: '12px',
        // color: "green",
        fontWeight: 400,
      },
  datePickerLabel: {
    position: 'relative',
    transform: 'scale(0.75)',
    lineHeight: 0.83,
    marginBottom: '-1px',
  },
  datePickerInput: {
    fontSize: '14px !important',
    height: '28px !important',
    marginTop: '-5px',
  },
  saveButtonDiv: {
    textAlign: 'right',
    marginRight: '20px',
    paddingBottom: '8px',
    marginTop: '-12px',
  },
  saveDiscardButtonSpan: {
    marginLeft: '24px',
  },
  relationshipSelectHolder: {
    width: '134px',
  },
  carrierRadioDiv: {
    display: 'inline-block',
    transform: 'scale(0.85)',
  },
  carrierRadio: {
    padding: 0,
  },
  carrierSearchDiv: {
    display: 'inline-block',
  },
});

const VobSeparator = ({ classes }) => {
  return <div className={classes.separator} />;
};

class VobTextField2 extends React.Component {
  state = {
    focused: false,
  };

  setFocused(b) {
    this.setState({ focused: b });
  }

  render() {
    const { form, label, stateFieldName } = this.props;
    return (
      <CrmTextField
        label={label}
        fullWidth
        value={form.state[stateFieldName]}
        onChange={(event) => form.changeTextField(stateFieldName, event.target.value)}
      />
    );
  }
}

const VobPhoneTextField = ({ classes, form, width }) => {
  return (
    <CrmTextField
      label="Phone"
      value={form.state.preauthCompanyPhone}
      onChange={(event) => form.changePhoneTextField('preauthCompanyPhone', event.target.value.trim())}
    />
  );
};

const VobSwitch = ({ classes, form, stateFieldName }) => {
  return (
    <Switch
      className={classes.vobSwitchRound}
      checked={form.state[stateFieldName]}
      onChange={() => form.handleSwitchChange(stateFieldName)}
      value="stateFieldName"
      color="primary"
    />
  );
};

const SnugglingSwitch = (props) => {
  const { label, ...rest } = props;
  return (
    <span>
      {label} <VobSwitch {...rest} />
    </span>
  );
};

const RelationshipKSelect = ({ classes, form, options }) => {
  return (
    <div className={classes.relationshipSelectHolder}>
      <KSelect
        name="relationship"
        fullWidth={true}
        displayEmpty={true}
        disabledOption="Choose..."
        liveOptions={options}
        value={form.state.relationship}
        onChange={(event) => form.handleRelationshipChange(event)}
      />
    </div>
  );
};

const PriorityKSelect = ({ classes, form, options }) => {
  return (
    <div className={classes.prioritySelectHolder}>
      <KSelect
        name="priority"
        label="Priority"
        fullWidth={true}
        displayEmpty={true}
        disabledOption="Choose..."
        liveOptions={options}
        value={form.state.priority}
        onChange={(event) => form.handlePriorityChange(event)}
      />
    </div>
  );
};

const handleAutocompleteChange = (form, newCarrierName) => {
  form.setState({ insuranceCarrier: newCarrierName });
};

const VobCarrierSearch = ({ form, value }) => {
  return (
    <div>
      <Autocomplete
        options={prefabCarrierNames}
        getOptionLabel={(name) => name}
        style={{ width: 200 }}
        renderInput={(params) => <TextField {...params} label="Search" variant="outlined" />}
        value={value || null}
        onChange={(event, carrierName) => handleAutocompleteChange(form, carrierName)}
      />
    </div>
  );
};

const PlanTypeKSelect = ({ classes, form, options }) => {
  return (
    <div className={classes.planTypeSelectHolder}>
      <KSelect
        name="planType"
        fullWidth={true}
        disabledOption="Choose..."
        liveOptions={options}
        value={form.state.planType}
        onChange={(event) => form.handleSelectChange(event)}
      />
    </div>
  );
};

const ReimbursementBasisKSelect = ({ classes, form, options }) => {
  return (
    <div className={classes.reimbursementBasisSelectHolder}>
      <KSelect
        name="reimbursementBasis"
        fullWidth={true}
        disabledOption="Choose..."
        liveOptions={options}
        value={form.state.reimbursementBasis}
        onChange={(event) => form.handleReimbursementBasisChange(event)}
      />
    </div>
  );
};

const NetworkStatusKSelect = ({ classes, form }) => {
  return (
    <div className={classes.newtworkStatusSelectHolder}>
      <KSelect
        name="networkStatus"
        fullWidth={true}
        disabledOption="Choose..."
        liveOptions={['In-Network', 'Out-of-Network']}
        value={form.state.networkStatus}
        onChange={(event) => form.handleSelectChange(event)}
      />
    </div>
  );
};

const VobBottomButton = ({ classes, outlined, tip, text, action }) => {
  return (
    <span className={classes.saveDiscardButtonSpan}>
      <Tooltip title={tip} placement="bottom" classes={{ tooltip: classes.tooltip }}>
        <CrmButton outlined={outlined} filled={!outlined} onClick={action}>
          {text}
        </CrmButton>
      </Tooltip>
    </span>
  );
};

class VobForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = props.vobInfo;
  }

  blankState = {
    verified: false,
    verificationDate: null,
    representativeName: '',
    verificationNumber: '',
    relationship: 'Choose...',
    customRelationship: '',
    holderFirstName: '',
    holderLastName: '',
    birthDate: null,
    holderStreet: '',
    holderCity: '',
    holderState: 'State',
    holderZip: '',
    insuranceCarrier: '',
    policyId: '',
    groupId: '',
    planType: 'Choose...',
    priority: '',
    effectiveDate: null,
    renewalDate: null,
    policyYear: 'calendarYear',
    policyIsActive: false,
    usingCobra: false,
    acceptsAssignment: false,
    cobOnFile: false,
    cobRequired: false,
    lastCobUpdated: null,
    individualDeductible: '',
    individualDeductibleMet: '',
    individualOop: '',
    individualOopMet: '',
    familyDeductible: '',
    familyDeductibleMet: '',
    familyOop: '',
    familyOopMet: '',
    copayAmount: '',
    coinsurancePct: '',
    limitStatus: '',
    reimbursementBasis: 'Choose...',
    deductibleAppliesToOop: false,
    freestandingCovored: false,
    jcahoRequred: false,
    outOfStateBenefits: false,
    stateLicenseRequred: false,
    exclusionsP: false,
    exclusionsText: '',
    preauthPenaltyP: false,
    preauthPenaltyText: '',
    preauthCompany: '',
    preauthCompanyPhone: '',
    networkStatus: 'Choose...',
    preauthRequred_OP: false,
    covered_OP: false,
    coinsurance_OP: '',
    copay_OP: '',
    benefitMax_OP: '',
    amountUsed_OP: '',
    preauthRequred_IOP: false,
    covered_IOP: false,
    coinsurance_IOP: '',
    copay_IOP: '',
    benefitMax_IOP: '',
    amountUsed_IOP: '',
    preauthRequred_PHP: false,
    covered_PHP: false,
    coinsurance_PHP: '',
    copay_PHP: '',
    benefitMax_PHP: '',
    amountUsed_PHP: '',
    preauthRequred_RTC: false,
    covered_RTC: false,
    coinsurance_RTC: '',
    copay_RTC: '',
    benefitMax_RTC: '',
    amountUsed_RTC: '',
    preauthRequred_Detox: false,
    covered_Detox: false,
    coinsurance_Detox: '',
    copay_Detox: '',
    benefitMax_Detox: '',
    amountUsed_Detox: '',
    notes: '',
    shouldSpecifyCarrier: false,
    specifiedCarrier: '',
  };

  saveCurrentStateToRedux(clientId) {
    this.props.addOrUpdateVobFormInfo({ personId: clientId, vobFormInfo: this.state });
    if (clientId !== 0) {
      // save it to the DB, too
      const uri = `/api/person/${clientId}/vobFormInfo`;
      const json = JSON.stringify(this.state);
      const apiStruct = this.createApiStructForUpdatingVobFormInfo(clientId, json);
      const request = createStdRequest(uri, 'post', apiStruct);
      apiFetch(this, request, () => {});
    }
  }

  createApiStructForUpdatingVobFormInfo(clientId, json) {
    return {
      clientId: clientId,
      vobFormInfo: json,
    };
  }

  changeTextField(stateFieldName, newValue) {
    const forbidWhiteSpace = this.shouldForbidWhiteSpaceInField(stateFieldName);
    const possiblyTrimmedValue = forbidWhiteSpace ? newValue.trim() : newValue;
    this.setState({ [stateFieldName]: possiblyTrimmedValue });
  }

  shouldForbidWhiteSpaceInField(stateFieldName) {
    return stateFieldName === 'holderZip';
  }

  changePhoneTextField(stateFieldName, newValue) {
    const oldValue = this.state[stateFieldName];
    const phChange = calculatePhoneNumberChange(oldValue, newValue);
    if (phChange.shouldUpdate) {
      this.setState({ [stateFieldName]: phChange.newValue });
    }
  }

  changeDate(propertyName, newDate) {
    this.setState({ [propertyName]: newDate });
  }

  handleSwitchChange(stateFieldName) {
    const oldState = this.state[stateFieldName];
    this.setState({ [stateFieldName]: !oldState });
    if (stateFieldName === 'verified') {
      if (oldState === true) {
        // now false, so clear the verfication date
        this.setState({ verificationDate: null });
      } else {
        // new true, so set the verification date
        this.setState({ verificationDate: DateTime.local().startOf('day') });
      }
    }
  }

  handleSelectChange(event) {
    const target = event.target;
    this.setState({ [target.name]: target.value });
  }

  handleRelationshipChange = (event) => {
    const newRelationship = event.target.value;
    if (this.state.relationship !== newRelationship) {
      this.populatePolicyHolderFields(newRelationship, this.state.relationship);
      this.setState({ relationship: newRelationship });
    }
  };

  handlePriorityChange = (event) => {
    const newPriority = event.target.value;
    if (this.state.priority !== newPriority) {
      this.setState({ priority: newPriority });
    }
  };

  populatePolicyHolderFields = (relationship, prevRelationship) => {
    if (relationship === 'Self') {
      const {
        clientFirstName,
        clientLastName,
        clientBirthDate,
        clientAddressLine1,
        clientCity,
        clientState,
        clientZip,
      } = this.props.clientInfo;
      const adjustedBDay = clientBirthDate < -60000000000000 ? '' : clientBirthDate;
      this.setState({
        holderFirstName: clientFirstName,
        holderLastName: clientLastName,
        birthDate: adjustedBDay,
        holderStreet: clientAddressLine1,
        holderCity: clientCity,
        holderState: clientState,
        holderZip: clientZip,
      });
    }

    if (prevRelationship === 'Self') {
      this.clearPolicyHolderFields();
    }
  };

  clearPolicyHolderFields = () => {
    this.setState({
      holderFirstName: '',
      holderLastName: '',
      birthDate: null,
      holderStreet: '',
      holderCity: '',
      holderState: '',
      holderZip: '',
    });
  };

  handleReimbursementBasisChange(event) {
    const selection = event.target.value;
    if (selection === 'Other...') {
      this.setState({ shouldShowNewReimbursementBasisModal: true });
    } else {
      this.setState({ [event.target.name]: selection });
    }
  }

  handleConfirmNewBasisModal(newBasis) {
    this.setState({ shouldShowNewReimbursementBasisModal: false });
    if (!newBasis) {
      return;
    }
    const uri = `/api/firm/${DA.getFirmId()}/addNewDropdownItem`;
    const apiStruct = {
      dropdownName: 'VOB Info: Reimbursement Basis',
      itemLabel: newBasis,
      position: -1,
    };
    const request = createStdRequest(uri, 'POST', apiStruct);
    const actionOnGoodResponse = (newDropdownSpec) => {
      this.setState({ reimbursementBasis: newBasis });
      // add to Redux, so it will appear next time
      this.props.updateFirmDropdown(newDropdownSpec);
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  closeNewBasisModal() {
    this.setState({ shouldShowNewReimbursementBasisModal: false });
  }

  getPriorityDropdownItems() {
    return ['Primary', 'Secondary', 'Tertiary', 'Rx'];
  }

  render() {
    const { classes, nOfM, collapseFunc, saveFunc } = this.props;
    const showCustomRelationship = this.state.relationship === 'Other...';
    const relationshipDropdownOptions = sortedDropdownItemLabels('VOB Info: Relationship to Insured');
    const planTypeDropdownOptions = sortedDropdownItemLabels('VOB Info: Plan Type');
    const reimbursementBasisDropdownOptions = sortedDropdownItemLabels('VOB Info: Reimbursement Basis');
    const carrierInputVisibility = this.state.shouldSpecifyCarrier ? 'visible' : 'hidden';
    const priorityDropdownOptions = this.getPriorityDropdownItems();

    const saveButton = (
      <VobBottomButton classes={classes} tip="Save VOB Info" text="Save" action={() => saveFunc(this.state)} />
    );

    return (
      <div>
        <ConfirmModal
          show={this.state.shouldShowDeletionConfirmationModal}
          title="Confirm Deletion"
          confirmButtonLabel="Delete"
          rejectButtonLabel="Cancel"
          onConfirm={this.state.deleteFunc}
          onReject={() => this.setState({ shouldShowDeletionConfirmationModal: false })}
        >
          <div>{this.state.deletionConfirmationQuery}</div>
        </ConfirmModal>

        <PromptModal
          show={!!this.state.shouldShowNewReimbursementBasisModal}
          title="Define a New Reimbursement Basis"
          inputLabel="New Basis"
          onConfirm={(newBasis) => this.handleConfirmNewBasisModal(newBasis)}
          onCancel={() => this.closeNewBasisModal()}
        />

        <div className={classes.vobForm}>
          <div className={classes.vobFormBody}>
            <div className={classes.vobFormTitleLine}>
              <div className={classes.topCloseIcon2}>
                <Tooltip
                  id="tooltip-hide"
                  key={0}
                  title="Collapse VOB Form"
                  placement="bottom"
                  classes={{ tooltip: classes.tooltip }}
                >
                  <Button className={classes.collapseButton} onClick={() => collapseFunc(this.state.guid, true)}>
                    <CollapseIcon className={classes.icon} />
                  </Button>
                </Tooltip>
              </div>
              <div className={classes.vobFormTitleDiv2}>Verification of Benefits {nOfM}</div>
            </div>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <SnugglingSwitch
                  classes={classes}
                  label="Benefits Successfully Verified?"
                  form={this}
                  stateFieldName="verified"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KDatePicker
                  label="Verification Date"
                  value={this.state.verificationDate}
                  onChange={(newDate) => this.setState({ verificationDate: newDate })}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <VobTextField2
                  classes={classes}
                  form={this}
                  label="Representative Name"
                  stateFieldName="representativeName"
                  minWidth="200px"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <VobTextField2
                  classes={classes}
                  form={this}
                  label="Verification Number"
                  stateFieldName="verificationNumber"
                  minWidth="200px"
                />
              </Grid>
            </Grid>

            <VobSeparator classes={classes} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                Relationship to Insured
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <RelationshipKSelect classes={classes} form={this} options={relationshipDropdownOptions} />
              </Grid>
              {showCustomRelationship && (
                <Grid item xs={12} sm={6} md={3}>
                  <VobTextField2
                    classes={classes}
                    form={this}
                    label="Specify Relationship"
                    stateFieldName="customRelationship"
                    minWidth="200px"
                  />
                </Grid>
              )}
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <VobTextField2
                  classes={classes}
                  form={this}
                  label="Policy Holder First Name"
                  stateFieldName="holderFirstName"
                  minWidth="200px"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <VobTextField2
                  classes={classes}
                  form={this}
                  label="Policy Holder Last Name"
                  stateFieldName="holderLastName"
                  minWidth="200px"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KDatePicker
                  label="Policy Holder Birthday"
                  value={this.state.birthDate}
                  onChange={(newDate) => this.setState({ birthDate: newDate })}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <VobTextField2
                  classes={classes}
                  form={this}
                  label="Policy Holder Address"
                  stateFieldName="holderStreet"
                  minWidth="200px"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <VobTextField2
                  classes={classes}
                  form={this}
                  label="City"
                  stateFieldName="holderCity"
                  minWidth="200px"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} style={{ marginTop: '8px' }}>
                <StateSelector
                  id="stateSelector"
                  value={this.state.holderState}
                  onChange={(newValue) => this.setState({ holderState: newValue })}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <VobTextField2 classes={classes} form={this} label="Zip" stateFieldName="holderZip" fixedWidth="70px" />
              </Grid>
            </Grid>

            <VobSeparator classes={classes} />

            <table className={classes.table3_1}>
              <tbody>
                <tr>
                  <td>Insurance Carrier:</td>
                  <td>
                    <div>
                      <div className={classes.carrierRadioDiv}>
                        <FormControlLabel
                          value={!this.state.shouldSpecifyCarrier}
                          control={
                            <CrmRadio
                              className={classes.carrierRadio}
                              checked={!this.state.shouldSpecifyCarrier}
                              onClick={(event) => this.setState({ shouldSpecifyCarrier: !event.target.checked })}
                            />
                          }
                          label="Search"
                          labelPlacement="end"
                        />
                      </div>
                      {!!this.state.shouldSpecifyCarrier || (
                        <div className={classes.carrierSearchDiv}>
                          <VobCarrierSearch classes={classes} form={this} value={this.state.insuranceCarrier} />
                        </div>
                      )}
                    </div>
                    <div>
                      <div className={classes.carrierRadioDiv}>
                        <FormControlLabel
                          value={this.state.shouldSpecifyCarrier}
                          control={
                            <CrmRadio
                              className={classes.carrierRadio}
                              checked={this.state.shouldSpecifyCarrier}
                              onClick={(event) => this.setState({ shouldSpecifyCarrier: event.target.checked })}
                            />
                          }
                          label="Specify"
                          labelPlacement="end"
                        />
                      </div>
                      <input
                        type="text"
                        value={this.state.specifiedCarrier}
                        onChange={(event) => this.setState({ specifiedCarrier: event.target.value })}
                        style={{ visibility: carrierInputVisibility, width: '204px' }}
                      />
                    </div>
                  </td>
                  <td>
                    <VobTextField2
                      classes={classes}
                      form={this}
                      label="Policy ID"
                      stateFieldName="policyId"
                      minWidth="120px"
                    />
                  </td>
                  <td>
                    <VobTextField2
                      classes={classes}
                      form={this}
                      label="Group ID"
                      stateFieldName="groupId"
                      minWidth="120px"
                    />
                  </td>
                  <td>Plan Type:</td>
                  <td>
                    <PlanTypeKSelect classes={classes} form={this} options={planTypeDropdownOptions} />
                  </td>
                </tr>
                <tr>
                  <td>
                    <PriorityKSelect classes={classes} form={this} options={priorityDropdownOptions} />
                  </td>
                  <td>
                    <KDatePicker
                      label="Effective Date"
                      value={this.state.effectiveDate}
                      onChange={(newDate) => this.setState({ effectiveDate: newDate })}
                    />
                  </td>
                  <td>
                    <KDatePicker
                      label="Renewal Date"
                      value={this.state.renewalDate}
                      onChange={(newDate) => this.setState({ renewalDate: newDate })}
                    />
                  </td>
                  <td>
                    <RadioGroup
                      name="policyYear"
                      value={this.state.policyYear}
                      onChange={(event) => this.setState({ policyYear: event.target.value })}
                      row
                      className={classes.policyYearRadios}
                    >
                      <FormControlLabel
                        value="calendarYear"
                        control={<CrmRadio />}
                        label="Calendar Year"
                        labelPlacement="end"
                      />
                      <FormControlLabel
                        value="planYear"
                        control={<CrmRadio />}
                        label="Plan Year"
                        labelPlacement="end"
                      />
                    </RadioGroup>
                  </td>
                  <td className={classes.policyPropCheckBoxes} colSpan="2">
                    <FormControlLabel
                      className={classes.vobCheckBoxStyle}
                      control={
                        <Checkbox
                          checked={this.state.policyIsActive}
                          onChange={(event) => this.setState({ policyIsActive: event.target.checked })}
                          value="policyIsActive"
                          color="primary"
                        />
                      }
                      label="Active"
                    />
                    &nbsp;&nbsp;&nbsp;&nbsp;
                    <FormControlLabel
                      className={classes.vobCheckBoxStyle}
                      control={
                        <Checkbox
                          checked={this.state.usingCobra}
                          onChange={(event) => this.setState({ usingCobra: event.target.checked })}
                          value="usingCobra"
                          color="primary"
                        />
                      }
                      label="COBRA"
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <table className={classes.table3_2}>
              <tbody>
                <tr>
                  <td className={classes.snuggleDatum}>
                    <SnugglingSwitch
                      classes={classes}
                      label="Accepts Assignment"
                      form={this}
                      stateFieldName="acceptsAssignment"
                    />
                  </td>
                  <td className={classes.snuggleDatum}>
                    <SnugglingSwitch classes={classes} label="COB on File" form={this} stateFieldName="cobOnFile" />
                  </td>
                  <td className={classes.snuggleDatum}>
                    <SnugglingSwitch classes={classes} label="COB Required" form={this} stateFieldName="cobRequired" />
                  </td>
                  <td>
                    <KDatePicker
                      label="Last COB Updated"
                      value={this.state.lastCobUpdated}
                      onChange={(newDate) => this.setState({ lastCobUpdated: newDate })}
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <table className={classes.table3_3}>
              <tbody>
                <tr>
                  <td>
                    <VobTextField2
                      classes={classes}
                      form={this}
                      label="Individual Deductible"
                      stateFieldName="individualDeductible"
                      minWidth="70px"
                    />
                  </td>
                  <td>
                    <VobTextField2
                      classes={classes}
                      form={this}
                      label="Met"
                      stateFieldName="individualDeductibleMet"
                      minWidth="70px"
                    />
                  </td>
                  <td>
                    <VobTextField2
                      classes={classes}
                      form={this}
                      label="Individual OOP"
                      stateFieldName="individualOop"
                      minWidth="70px"
                    />
                  </td>
                  <td>
                    <VobTextField2
                      classes={classes}
                      form={this}
                      label="Met"
                      stateFieldName="individualOopMet"
                      minWidth="70px"
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <VobTextField2
                      classes={classes}
                      form={this}
                      label="Family Deductible"
                      stateFieldName="familyDeductible"
                      minWidth="70px"
                    />
                  </td>
                  <td>
                    <VobTextField2
                      classes={classes}
                      form={this}
                      label="Met"
                      stateFieldName="familyDeductibleMet"
                      minWidth="70px"
                    />
                  </td>
                  <td>
                    <VobTextField2
                      classes={classes}
                      form={this}
                      label="Family OOP"
                      stateFieldName="familyOop"
                      minWidth="70px"
                    />
                  </td>
                  <td>
                    <VobTextField2
                      classes={classes}
                      form={this}
                      label="Met"
                      stateFieldName="familyOopMet"
                      minWidth="70px"
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <VobTextField2
                      classes={classes}
                      form={this}
                      label="Copay Amount"
                      stateFieldName="copayAmount"
                      minWidth="70px"
                    />
                  </td>
                  <td colSpan="2">
                    <VobTextField2
                      classes={classes}
                      form={this}
                      label="Coinsurance % (Patient Share)"
                      stateFieldName="coinsurancePct"
                      minWidth="240px"
                    />
                  </td>
                  <td>
                    <VobTextField2
                      classes={classes}
                      form={this}
                      label="Limit Status"
                      stateFieldName="limitStatus"
                      minWidth="70px"
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <table className={classes.table3_4}>
              <tbody>
                <tr>
                  <td>Reimbursement Basis</td>
                  <td>
                    <ReimbursementBasisKSelect
                      classes={classes}
                      form={this}
                      options={reimbursementBasisDropdownOptions}
                    />
                  </td>
                  <td>
                    <SnugglingSwitch
                      classes={classes}
                      label="Does deductible apply to out-of-pocket max?"
                      form={this}
                      stateFieldName="deductibleAppliesToOop"
                    />
                  </td>
                  <td>
                    <SnugglingSwitch
                      classes={classes}
                      label="Are free-standing facilities covered?"
                      form={this}
                      stateFieldName="freestandingCovored"
                    />
                  </td>
                </tr>
                <tr>
                  <td>Is JCAHO/CARF required?</td>
                  <td>
                    <VobSwitch classes={classes} form={this} stateFieldName="jcahoRequred" />
                  </td>
                  <td>
                    <SnugglingSwitch
                      classes={classes}
                      label="Out of state benefits?"
                      form={this}
                      stateFieldName="outOfStateBenefits"
                    />
                  </td>
                  <td>
                    <SnugglingSwitch
                      classes={classes}
                      label="State license required?"
                      form={this}
                      stateFieldName="stateLicenseRequred"
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <table className={classes.table3_4}>
              <tbody>
                <tr>
                  <td>Exclusions?</td>
                  <td>
                    <VobSwitch classes={classes} form={this} stateFieldName="exclusionsP" />
                  </td>
                  <td>
                    <VobTextField2
                      classes={classes}
                      form={this}
                      label="Exclusions"
                      stateFieldName="exclusionsText"
                      minWidth="700px"
                    />
                  </td>
                </tr>
                <tr>
                  <td>Pre-Auth penalty?</td>
                  <td>
                    <VobSwitch classes={classes} form={this} stateFieldName="preauthPenaltyP" />
                  </td>
                  <td>
                    <VobTextField2
                      classes={classes}
                      form={this}
                      label="Penalty"
                      stateFieldName="preauthPenaltyText"
                      minWidth="700px"
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <table className={classes.table3_5}>
              <tbody>
                <tr>
                  <td>
                    <VobTextField2
                      classes={classes}
                      form={this}
                      label="Pre-Auth Company"
                      stateFieldName="preauthCompany"
                      minWidth="200px"
                    />
                  </td>
                  <td>
                    <VobPhoneTextField classes={classes} form={this} stateFieldName="preauthCompanyPhone" />
                  </td>
                </tr>
              </tbody>
            </table>

            <VobSeparator classes={classes} />

            <table className={classes.table4}>
              <tbody>
                <tr>
                  <td>Network Status</td>
                  <td className={classes.networkStatusSelectDatum}>
                    <NetworkStatusKSelect classes={classes} form={this} />
                  </td>
                </tr>
              </tbody>
            </table>

            <table className={classes.table5}>
              <tbody>
                <tr className={classes.table5ColumnHeaders}>
                  <td className={classes.locLabel}>Level of Care</td>
                  <td>Pre-Auth Required?</td>
                  <td>Covered?</td>
                  <td>Co-Insurance %</td>
                  <td>Co-pay Amount</td>
                  <td>Benefit Max</td>
                  <td>Amount Used</td>
                </tr>
                <tr>
                  <td className={classes.locLabel}>OP</td>
                  <td>
                    <VobSwitch classes={classes} form={this} stateFieldName="preauthRequred_OP" />
                  </td>
                  <td>
                    <VobSwitch classes={classes} form={this} stateFieldName="covered_OP" />
                  </td>
                  <td>
                    <VobTextField2 classes={classes} form={this} stateFieldName="coinsurance_OP" fixedWidth="80px" />
                  </td>
                  <td>
                    <VobTextField2 classes={classes} form={this} stateFieldName="copay_OP" fixedWidth="80px" />
                  </td>
                  <td>
                    <VobTextField2 classes={classes} form={this} stateFieldName="benefitMax_OP" fixedWidth="80px" />
                  </td>
                  <td>
                    <VobTextField2 classes={classes} form={this} stateFieldName="amountUsed_OP" fixedWidth="80px" />
                  </td>
                </tr>
                <tr>
                  <td className={classes.locLabel}>IOP</td>
                  <td>
                    <VobSwitch classes={classes} form={this} stateFieldName="preauthRequred_IOP" />
                  </td>
                  <td>
                    <VobSwitch classes={classes} form={this} stateFieldName="covered_IOP" />
                  </td>
                  <td>
                    <VobTextField2 classes={classes} form={this} stateFieldName="coinsurance_IOP" fixedWidth="80px" />
                  </td>
                  <td>
                    <VobTextField2 classes={classes} form={this} stateFieldName="copay_IOP" fixedWidth="80px" />
                  </td>
                  <td>
                    <VobTextField2 classes={classes} form={this} stateFieldName="benefitMax_IOP" fixedWidth="80px" />
                  </td>
                  <td>
                    <VobTextField2 classes={classes} form={this} stateFieldName="amountUsed_IOP" fixedWidth="80px" />
                  </td>
                </tr>
                <tr>
                  <td className={classes.locLabel}>PHP</td>
                  <td>
                    <VobSwitch classes={classes} form={this} stateFieldName="preauthRequred_PHP" />
                  </td>
                  <td>
                    <VobSwitch classes={classes} form={this} stateFieldName="covered_PHP" />
                  </td>
                  <td>
                    <VobTextField2 classes={classes} form={this} stateFieldName="coinsurance_PHP" fixedWidth="80px" />
                  </td>
                  <td>
                    <VobTextField2 classes={classes} form={this} stateFieldName="copay_PHP" fixedWidth="80px" />
                  </td>
                  <td>
                    <VobTextField2 classes={classes} form={this} stateFieldName="benefitMax_PHP" fixedWidth="80px" />
                  </td>
                  <td>
                    <VobTextField2 classes={classes} form={this} stateFieldName="amountUsed_PHP" fixedWidth="80px" />
                  </td>
                </tr>
                <tr>
                  <td className={classes.locLabel}>RTC</td>
                  <td>
                    <VobSwitch classes={classes} form={this} stateFieldName="preauthRequred_RTC" />
                  </td>
                  <td>
                    <VobSwitch classes={classes} form={this} stateFieldName="covered_RTC" />
                  </td>
                  <td>
                    <VobTextField2 classes={classes} form={this} stateFieldName="coinsurance_RTC" fixedWidth="80px" />
                  </td>
                  <td>
                    <VobTextField2 classes={classes} form={this} stateFieldName="copay_RTC" fixedWidth="80px" />
                  </td>
                  <td>
                    <VobTextField2 classes={classes} form={this} stateFieldName="benefitMax_RTC" fixedWidth="80px" />
                  </td>
                  <td>
                    <VobTextField2 classes={classes} form={this} stateFieldName="amountUsed_RTC" fixedWidth="80px" />
                  </td>
                </tr>
                <tr>
                  <td className={classes.locLabel}>Detox</td>
                  <td>
                    <VobSwitch classes={classes} form={this} stateFieldName="preauthRequred_Detox" />
                  </td>
                  <td>
                    <VobSwitch classes={classes} form={this} stateFieldName="covered_Detox" />
                  </td>
                  <td>
                    <VobTextField2 classes={classes} form={this} stateFieldName="coinsurance_Detox" fixedWidth="80px" />
                  </td>
                  <td>
                    <VobTextField2 classes={classes} form={this} stateFieldName="copay_Detox" fixedWidth="80px" />
                  </td>
                  <td>
                    <VobTextField2 classes={classes} form={this} stateFieldName="benefitMax_Detox" fixedWidth="80px" />
                  </td>
                  <td>
                    <VobTextField2 classes={classes} form={this} stateFieldName="amountUsed_Detox" fixedWidth="80px" />
                  </td>
                </tr>
              </tbody>
            </table>

            <div className={classes.notesHolder}>
              <div className={classes.notesLabelDiv}>VOB Notes:</div>
              <div>
                <CrmTextField
                  className={classes.notesField}
                  multiline
                  inputProps={{ className: classes.notesFieldInput }}
                  value={this.state.notes}
                  onChange={(event) => this.changeTextField('notes', event.target.value)}
                />
              </div>
            </div>

            <div className={classes.saveButtonDiv}>{saveButton}</div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({});

const actionProducers = { addOrUpdateVobFormInfo, updateFirmDropdown };

export default connect(mapStateToProps, actionProducers)(withStyles(localStyles)(VobForm));
